import React, { useState, useMemo } from 'react';
import { connect } from 'react-redux';
import moment from 'moment';
import MUIDataTable from 'mui-datatables';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Divider,
  Modal,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@material-ui/core';

import CustomToolbar from 'components/CustomToolbar/CustomToolbar';
import RefundProductsModal from 'components/SalesOrders/RefundProductsModal';

import { setNewSOInfo, clearNewSO, deleteSalesOrder } from 'redux/actions';
import { admin } from 'variables/userAccountTypes';

const SalesOrdersTable = (props) => {

  const [rowsSelected, setRowsSelected] = useState([]);
  const [rowsExpanded, setRowsExpanded] = useState([]);

  const [openCancelModal, setOpenCancelModal] = useState(false);
  const [openRefundsModal, setOpenRefundsModal] = useState(false);

  const filteredSalesOrders = useMemo(() => {
    //if the user is single or standard, only that user's SalesOrders will be shown,
    //else all SalesOrders will be shown.
    if(props.activeUserType === admin) return props.salesOrders.filter(salesOrder => salesOrder.userId === props.user.standard_users[props.userTabValue].id);
    else return props.salesOrders;

  }, [props.salesOrders, props.userTabValue, props.activeUserType, props.user]);

  const tableData = useMemo(() => {
    const initialTableData = [];

    for ( let i = 0; i < filteredSalesOrders.length; i++ ) {
      const salesOrder = filteredSalesOrders[i];
      const salesOrderCustomer = props.customers.find(customer => customer.id === salesOrder.customerId);
      initialTableData.push({
        id: salesOrder.id,
        displayId: salesOrder.displayId,
        name: salesOrderCustomer ? salesOrderCustomer.name : "",
        phone: salesOrderCustomer ? salesOrderCustomer.phone : "",
        date: moment(salesOrder.createdAt).format('DD/MM/YYYY'),
        products: salesOrder.products,
        total: salesOrder.total
      });
    }

    return initialTableData;
    
  }, [filteredSalesOrders, props.customers]);

  const handleRefundsModalOpen = () => {
    const salesOrderInfo = filteredSalesOrders[rowsSelected[0]];
    const salesOrderProducts = salesOrderInfo.products.map(salesOrderProduct => {
      const productInfo = props.products.find(product => product.id === salesOrderProduct.productId);
      return {
        displayId: productInfo.displayId,
        name: productInfo.name,
        company: productInfo.company,
        ...salesOrderProduct
      }
    });
    props.setNewSOInfo({
      displayId: salesOrderInfo.displayId,
      customerId: salesOrderInfo.customerId,
      soProducts: salesOrderProducts,
      totalVat: salesOrderInfo.totalVat,
      total: salesOrderInfo.total,
      paymentMethod: salesOrderInfo.paymentMethod
    });

    setOpenRefundsModal(true);
  };

  const handleRefundsModalClose = (openInvoice) => {
    setOpenRefundsModal(false);
    setRowsSelected([]);
    if(openInvoice) props.setOpenInvoiceModal(true);
    else props.clearNewSO();
  };
  
  const onRowSelectionChange = (curr, all, rowsSelected) => {
    setRowsSelected(rowsSelected);
  };

  const onRowExpansionChange = (currentRowsExpanded, allRowsExpanded) => {
    setRowsExpanded(allRowsExpanded.map(row => row.dataIndex));
  };
  
  const customToolbarSelect = (selectedRows) => {
    return(
      <CustomToolbar
        switchy={0}
        handleModalOpen={handleCancelModalOpen}
        handleRefundsModalOpen={handleRefundsModalOpen}
      />
    )
  };
  
  const columns = [
    { label: "ID", name: "id", options: { display: 'excluded', filter: false, sort: false, viewColumns: false } },
    { label: "Order ID", name: "displayId", options: { filter: false, sort: false, viewColumns: false } },
    { label: "Customer Name", name: "name", options: { filter: true, sort: true } },
    { label: "Customer Phone", name: "phone", options: { filter: false, sort: false } },
    { label: "Date", name: "date", options: { filter: true, sort: true } },
    { label: "Total Amount", name: "total", options: { filter: false, sort: false } }
  ];
  
  const options = {
    filterType: 'dropdown',
    responsive: 'standard',
    rowsSelected,
    rowsExpanded,
    onRowSelectionChange,
    onRowExpansionChange,
    customToolbarSelect,
    selectableRows: !(props.activeUserType === admin) ? 'single' : 'none',
    expandableRows: true,
    expandableRowsHeader: false,
    expandableRowsOnClick: false,
    isRowExpandable: (dataIndex, expandedRows) => {
      // Prevent expand/collapse of any row if there are 3 rows expanded already (but allow those already expanded to be collapsed)
      if (expandedRows.data.length > 3 && expandedRows.data.filter(d => d.dataIndex === dataIndex).length === 0)
        return false;
      return true;
    },
    renderExpandableRow: (rowData, rowMeta) => {
      const colSpan = rowData.length + 1;
      const salesOrderId = rowData[0];
      const rowProducts = tableData.find(salesOrder => salesOrder.id === salesOrderId).products;
      return (
        <tr>
          <td colSpan={colSpan}>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Product ID</TableCell>
                    <TableCell>Product Name</TableCell>
                    <TableCell>Product Brand</TableCell>
                    <TableCell>Unit Price</TableCell>
                    <TableCell>Product Quantity</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rowProducts.map((rowProduct) => {
                    const productInfo = props.products.find(product => product.id === rowProduct.productId);
                    return (
                      <TableRow key={productInfo ? productInfo.displayId : Math.floor(Math.random() * 1000).toString()}>
                        <TableCell>
                          {productInfo ? productInfo.displayId : ""}
                        </TableCell>
                        <TableCell>{productInfo ? productInfo.name : ""}</TableCell>
                        <TableCell>{productInfo ? productInfo.company : ""}</TableCell>
                        <TableCell>{rowProduct.unitPrice}</TableCell>
                        <TableCell>{rowProduct.quantity}</TableCell>
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </td>
        </tr>
      );
    }
  };

  const handleCancelModalOpen = () => {
    setOpenCancelModal(true);
  };

  const handleCancelModalClose = () => {
    setOpenCancelModal(false);
    setRowsSelected([]);
  };

  const cancelSalesOrder = () => {
    const salesOrderInfo = filteredSalesOrders[rowsSelected[0]];

    const updates = {
      products: salesOrderInfo.products,
      paymentMethod: salesOrderInfo.paymentMethod,
      customerId: salesOrderInfo.customerId,
      decrementAmount: salesOrderInfo.total
    };
    props.deleteSalesOrder(salesOrderInfo.id, updates, props.user.adminId);
    
    setOpenCancelModal(false);
    setRowsSelected([]);
  };

  const renderCancelModal = () => (
    <Modal
      open={openCancelModal}
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        marginBottom: '20%'
      }}
    >
      <Card>
        <CardHeader
          title="Cancel Sales Order ?"
        />
        <Divider />
        <CardContent>
          <p> The Following Sales Order will be Cancelled... </p>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center'
            }}
          >
            <Button
              color="secondary"
              variant="contained"
              onClick={cancelSalesOrder}
            >
              Cancel
            </Button>
            <Button sx={{ mx: 1 }} onClick={handleCancelModalClose}>
              Close
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Modal>
  );

  return (
    <React.Fragment>
      <MUIDataTable
        title={"Sales Orders"} 
        data={tableData}
        columns={columns}
        options={options}
      />
      { openRefundsModal ?
      <RefundProductsModal
        open={openRefundsModal}
        salesOrders={filteredSalesOrders}
        products={props.products}
        user={props.user}
        closeModal={handleRefundsModalClose}
      /> : null }
      { openCancelModal ? renderCancelModal() : null }
    </React.Fragment>
  )
};

export default connect (null, {setNewSOInfo, clearNewSO, deleteSalesOrder}) (SalesOrdersTable);